import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import useSessionStore from '../../state/session';
import useActiveLocationStore from '../../state/locationScope';
import type { ClientSummary } from '../../domain/clients';
import {
  CreateRecordWizard,
  CreateWizardBasics,
  CreateWizardClientPicker,
  CreateWizardLocationSelect,
} from '../../components/create/CreateRecordWizard';
import {
  checkEventCodeExists,
  consumeOrderRefHold,
  createEventBundle,
  fetchDefaultVenueForSite,
  fetchNextOrderRefForSite,
  fetchSiteOptions,
  fetchVenueRooms,
  releaseOrderRefHold,
  searchVenues,
  type RoomDraft,
  type VenueOption,
  type VenueRoomTemplate,
} from '../../data/eventWizard';
import { fetchVenueDetail } from '../../data/venues';
import type { PriceCheckEventDraft } from '@/domain/events/priceCheckDraft';
import { SaveIcon } from '../../ui/SaveIcon';
import { useToasts } from '../../ui/ToastProvider';
import { eventCreationDateErrors } from '../../domain/events/creationDates';
import './CreateEventWizard.css';

function parseDate(dateStr: string) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((part) => Number(part));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function isBusinessDay(d: Date) {
  const wd = d.getUTCDay();
  return wd !== 0 && wd !== 6;
}
function previousBusinessDay(d: Date) {
  const cur = new Date(d.getTime());
  cur.setUTCDate(cur.getUTCDate() - 1);
  while (!isBusinessDay(cur)) cur.setUTCDate(cur.getUTCDate() - 1);
  return cur;
}
function nextBusinessDay(d: Date) {
  const cur = new Date(d.getTime());
  cur.setUTCDate(cur.getUTCDate() + 1);
  while (!isBusinessDay(cur)) cur.setUTCDate(cur.getUTCDate() + 1);
  return cur;
}
interface WarehouseScheduleDefaults {
  fill_date: string;
  fill_time: string;
  ship_date: string;
  ship_time: string;
  return_date: string;
  return_time: string;
}
function computeWarehouseScheduleDefaults(
  startDateStr: string | null,
  endDateStr: string | null,
): WarehouseScheduleDefaults | null {
  if (!startDateStr) return null;
  const start = parseDate(startDateStr);
  if (!start) return null;
  const end = (endDateStr ? parseDate(endDateStr) : null) || start;
  const prevBiz = previousBusinessDay(start);
  const nextBiz = nextBusinessDay(end);
  const FILL_TIME = '09:00:00';
  const SHIP_TIME = '15:00:00';
  const RETURN_TIME = '11:00:00';
  return {
    fill_date: prevBiz.toISOString().slice(0, 10),
    fill_time: FILL_TIME,
    ship_date: prevBiz.toISOString().slice(0, 10),
    ship_time: SHIP_TIME,
    return_date: nextBiz.toISOString().slice(0, 10),
    return_time: RETURN_TIME,
  };
}

interface WizardProps {
  onCreated: (ev: any) => void;
  onCancel: () => void;
  /** When set, the venue step will have this venue pre-selected (e.g. from venue library "Create Event"). */
  initialVenueId?: string | null;
  /** Optional prefill payload from Home price/availability check. */
  initialPriceCheckDraft?: PriceCheckEventDraft | null;
}

const steps = ['Basics', 'Venue', 'Rooms', 'Review'];

export default function CreateEventWizard({ onCreated, onCancel, initialVenueId, initialPriceCheckDraft }: WizardProps) {
  const { push: pushToast } = useToasts();
  const [step, setStep] = React.useState(0);
  const [selectedClient, setSelectedClient] = React.useState<ClientSummary | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  const [name, setName] = React.useState('');
  const [eventCode, setEventCode] = React.useState('');
  const [eventCodeLoading, setEventCodeLoading] = React.useState(false);
  const [eventCodeError, setEventCodeError] = React.useState<string | null>(null);
  const [isEditingCode, setIsEditingCode] = React.useState(false);
  const [codeInput, setCodeInput] = React.useState('');
  const [codeSaving, setCodeSaving] = React.useState(false);
  const [eventStart, setEventStart] = React.useState('');
  const [eventEnd, setEventEnd] = React.useState('');
  const [datesAttempted, setDatesAttempted] = React.useState(false);
  const dateErrors = eventCreationDateErrors(eventStart, eventEnd);
  const startInputRef = React.useRef<HTMLInputElement>(null);
  const endInputRef = React.useRef<HTMLInputElement>(null);
  const stepHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const submitInFlightRef = React.useRef(false);
  const [customerPickup, setCustomerPickup] = React.useState(false);
  const claimedCodeRef = React.useRef<{ code: string; locationId: string } | null>(null);

  const allowedLocationIds = useSessionStore((s) => s.allowedLocationIds);
  const profileHomeLocationId = useSessionStore((s) => s.profile?.home_location_id ?? null);
  const sessionBooting = useSessionStore((s) => s.booting);
  const activeLocationId = useActiveLocationStore((state) => state.activeLocationId);
  const locationInitialised = useActiveLocationStore((state) => state.initialised);
  const setActiveLocationId = useActiveLocationStore((state) => state.setActiveLocationId);
  const allowedKey = React.useMemo(() => (allowedLocationIds || []).filter(Boolean).join(','), [allowedLocationIds]);

  // Track whether we need to show the location selection prompt
  // This happens when "all locations" is selected (activeLocationId is null)
  const [needsLocationPrompt, setNeedsLocationPrompt] = React.useState<boolean | null>(null);
  const [promptLocationId, setPromptLocationId] = React.useState<string | null>(null);

  const siteOptionsQuery = useQuery({
    queryKey: ['site-options', allowedKey],
    enabled: !sessionBooting,
    queryFn: async () => {
      const { data, error } = await fetchSiteOptions(allowedLocationIds);
      if (error) throw error;
      return data;
    },
  });
  const siteOptions = React.useMemo(() => siteOptionsQuery.data ?? [], [siteOptionsQuery.data]);
  const siteOptionsLoading = siteOptionsQuery.isFetching || sessionBooting;
  const siteOptionsLoaded = siteOptionsQuery.isFetched && !sessionBooting;
  const [selectedSiteId, setSelectedSiteId] = React.useState<string | null>(activeLocationId ?? null);
  const handleSelectSite = React.useCallback(
    (value: string | null) => {
      setSelectedSiteId(value);
      setActiveLocationId(value);
      const nextSite = siteOptions.find(option => option.location_id === value);
      if (!nextSite?.customer_pickup_enabled) setCustomerPickup(false);
    },
    [setActiveLocationId, siteOptions],
  );
  const priceCheckPrefillAppliedRef = React.useRef(false);

  // Initialize location prompt state once site options are loaded
  React.useEffect(() => {
    if (!locationInitialised || !siteOptionsLoaded || needsLocationPrompt !== null) return;
    
    // If activeLocationId is null (all locations selected), show the prompt
    if (activeLocationId === null) {
      setNeedsLocationPrompt(true);
      // Pre-populate with user's default home location if available and accessible
      const availableIds = new Set(siteOptions.map((opt) => opt.location_id));
      if (profileHomeLocationId && availableIds.has(profileHomeLocationId)) {
        setPromptLocationId(profileHomeLocationId);
      } else if (siteOptions.length > 0) {
        setPromptLocationId(siteOptions[0].location_id);
      }
    } else {
      setNeedsLocationPrompt(false);
    }
  }, [locationInitialised, siteOptionsLoaded, activeLocationId, profileHomeLocationId, siteOptions, needsLocationPrompt]);

  // Handle confirming the location from the prompt
  const handleConfirmLocation = React.useCallback(() => {
    if (!promptLocationId) return;
    setActiveLocationId(promptLocationId);
    setSelectedSiteId(promptLocationId);
    setNeedsLocationPrompt(false);
  }, [promptLocationId, setActiveLocationId]);

  const [venueSearch, setVenueSearch] = React.useState('');
  const [venueQuery, setVenueQuery] = React.useState('');
  const [venueSearchComposing, setVenueSearchComposing] = React.useState(false);
  const venueSearchRef = React.useRef<HTMLInputElement>(null);
  const venueChosenRef = React.useRef(false);
  const [selectedVenue, setSelectedVenue] = React.useState<VenueOption | null>(null);
  const initialVenueSetRef = React.useRef(false);
  const [createVenueMode, setCreateVenueMode] = React.useState(false);

  const initialVenueQuery = useQuery({
    queryKey: ['venue', initialVenueId],
    enabled: Boolean(initialVenueId),
    queryFn: async () => {
      if (!initialVenueId) return null;
      const { data, error } = await fetchVenueDetail(initialVenueId);
      if (error) throw error;
      return data;
    },
  });

  React.useEffect(() => {
    if (!initialVenueId || initialVenueSetRef.current || !initialVenueQuery.data) return;
    const row = initialVenueQuery.data;
    setSelectedVenue({
      id: row.id,
      venue_name: row.venue_name ?? '',
      city: row.city ?? null,
      state: row.state ?? null,
      is_outdoor: row.is_outdoor ?? null,
      is_ita_internal: row.is_ita_internal ?? null,
    });
    venueChosenRef.current = true;
    initialVenueSetRef.current = true;
  }, [initialVenueId, initialVenueQuery.data]);
  const [newVenue, setNewVenue] = React.useState({ venue_name: '', city: '', state: '', is_outdoor: false });
  const [rooms, setRooms] = React.useState<RoomDraft[]>([]);
  const [roomName, setRoomName] = React.useState('');

  React.useEffect(() => {
    if (!initialPriceCheckDraft || priceCheckPrefillAppliedRef.current) return;
    priceCheckPrefillAppliedRef.current = true;
    setEventStart(initialPriceCheckDraft.startDate || '');
    setEventEnd(initialPriceCheckDraft.endDate || '');
    if (initialPriceCheckDraft.siteLocationId) {
      handleSelectSite(initialPriceCheckDraft.siteLocationId);
    }
    setName((prev) => {
      if (prev.trim()) return prev;
      const dateLabel = initialPriceCheckDraft.startDate || new Date().toISOString().slice(0, 10);
      return `Price Check ${dateLabel}`;
    });
  }, [handleSelectSite, initialPriceCheckDraft]);

  React.useEffect(() => {
    if (venueSearchComposing) return;
    if (!venueSearch.trim()) { setVenueQuery(''); return; }
    const timer = window.setTimeout(() => setVenueQuery(venueSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [venueSearch, venueSearchComposing]);

  React.useEffect(() => { stepHeadingRef.current?.focus(); }, [step]);

  const venueResultsQuery = useQuery({
    queryKey: ['venue-search', venueQuery],
    enabled: step === 1 && !createVenueMode,
    queryFn: async () => {
      const { data, error } = await searchVenues(venueQuery);
      if (error) throw error;
      return data;
    },
  });
  const venueResults = React.useMemo(() => venueResultsQuery.data ?? [], [venueResultsQuery.data]);

  const roomTemplatesQuery = useQuery({
    queryKey: ['venue-rooms', selectedVenue?.id],
    enabled: step === 2 && Boolean(selectedVenue?.id),
    queryFn: async () => {
      const { data, error } = await fetchVenueRooms(selectedVenue?.id || '');
      if (error) throw error;
      return data;
    },
  });
  const roomTemplates = roomTemplatesQuery.data || [];

  const defaultVenueQuery = useQuery({
    queryKey: ['default-venue', selectedSiteId],
    enabled: step === 1 && Boolean(selectedSiteId),
    queryFn: async () => {
      const { data, error } = await fetchDefaultVenueForSite(selectedSiteId || '');
      if (error) throw error;
      return data;
    },
  });

  React.useEffect(() => {
    if (step !== 1) return;
    if (!selectedSiteId || createVenueMode || initialVenueId) return;
    if (selectedVenue || venueChosenRef.current) return;
    const v = defaultVenueQuery.data;
    if (v) { setSelectedVenue(v); venueChosenRef.current = true; }
  }, [defaultVenueQuery.data, selectedSiteId, selectedVenue, step, createVenueMode, initialVenueId]);

  React.useEffect(() => {
    setRooms([]);
    setRoomName('');
  }, [createVenueMode, selectedVenue?.id]);

  const releaseOrderCodeHold = React.useCallback(
    async (hold?: { code: string; locationId: string } | null) => {
      const target = hold ?? claimedCodeRef.current;
      if (!target) return;
      if (!hold || (claimedCodeRef.current && claimedCodeRef.current.code === target.code && claimedCodeRef.current.locationId === target.locationId)) {
        claimedCodeRef.current = null;
      }
      try {
        await releaseOrderRefHold(target);
      } catch (err) {
        console.warn('Failed to release held order number', err);
      }
    },
    [],
  );

  const consumeOrderCodeHoldLocal = React.useCallback(async () => {
    const target = claimedCodeRef.current;
    if (!target) return;
    try {
      await consumeOrderRefHold(target);
      claimedCodeRef.current = null;
    } catch (err) {
      console.warn('Failed to mark held order number as used', err);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedSiteId) {
        await releaseOrderCodeHold();
        if (!cancelled) {
          setEventCode('');
          setEventCodeError(null);
          setEventCodeLoading(false);
        }
        return;
      }
      if (claimedCodeRef.current && claimedCodeRef.current.locationId !== selectedSiteId) {
        await releaseOrderCodeHold();
      }
      if (cancelled) return;
      setEventCodeLoading(true);
      setEventCodeError(null);
      try {
        const next = await fetchNextOrderRefForSite(selectedSiteId);
        if (cancelled) return;
        if (next) {
          setEventCode(next);
          claimedCodeRef.current = { code: next, locationId: selectedSiteId };
        } else {
          setEventCode('');
          setEventCodeError('No order prefix configured for this site.');
          claimedCodeRef.current = null;
        }
      } catch (err: any) {
        if (cancelled) return;
        setEventCode('');
        setEventCodeError(err?.message || 'Unable to generate an order number for this site.');
        claimedCodeRef.current = null;
      } finally {
        if (!cancelled) setEventCodeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId, releaseOrderCodeHold]);

  React.useEffect(() => {
    return () => {
      void releaseOrderCodeHold();
    };
  }, [releaseOrderCodeHold]);

  const handleCancel = React.useCallback(() => {
    void (async () => {
      await releaseOrderCodeHold();
      onCancel();
    })();
  }, [onCancel, releaseOrderCodeHold]);

  React.useEffect(() => {
    // Wait until the prompt decision is complete, and skip automatic selection
    // whenever the user still needs to confirm a location.
    if (needsLocationPrompt !== false) return;
    if (!locationInitialised || !siteOptionsLoaded) return;
    const availableIds = new Set(siteOptions.map((opt) => opt.location_id));
    let nextId: string | null = null;
    if (activeLocationId && availableIds.has(activeLocationId)) nextId = activeLocationId;
    else if (profileHomeLocationId && availableIds.has(profileHomeLocationId)) nextId = profileHomeLocationId;
    else if (siteOptions.length > 0) nextId = siteOptions[0]?.location_id ?? null;
    if (nextId === (selectedSiteId ?? null)) return;
    handleSelectSite(nextId);
  }, [
    locationInitialised,
    siteOptions,
    activeLocationId,
    profileHomeLocationId,
    selectedSiteId,
    handleSelectSite,
    siteOptionsLoaded,
    needsLocationPrompt,
  ]);

  const addTemplateRoom = React.useCallback(
    (rt: VenueRoomTemplate) => setRooms((r) => [...r, { name: rt.name, type: rt.type || undefined, capacity: rt.capacity ?? undefined, templateId: rt.id }]),
    [],
  );
  const addCustomRoom = React.useCallback(() => {
    if (!roomName.trim()) return;
    setRooms((r) => [...r, { name: roomName.trim() }]);
    setRoomName('');
  }, [roomName]);
  const removeRoom = React.useCallback((idx: number) => setRooms((r) => r.filter((_, i) => i !== idx)), []);

  function canNext() {
    if (step === 0) return !!name.trim() && !!eventCode.trim() && !!selectedSiteId && !eventCodeLoading && !eventCodeError && !isEditingCode;
    if (step === 1) return createVenueMode ? !!newVenue.venue_name.trim() : customerPickup || !!selectedVenue;
    return true;
  }

  function nextStep() {
    if (!canNext() || saving) return;
    if (step === 0) {
      setDatesAttempted(true);
      if (dateErrors.start || dateErrors.end) {
        (dateErrors.start ? startInputRef : endInputRef).current?.focus();
        return;
      }
    }
    setStep(current => current + 1);
  }

  const createEventMutation = useMutation({
    mutationFn: async () => {
      const errors = eventCreationDateErrors(eventStart, eventEnd);
      if (errors.start || errors.end) throw new Error(errors.start || errors.end);
      const startDate = eventStart;
      const computedEnd = eventEnd;
      const eventPayload = {
        name,
        event_code: eventCode.trim(),
        show_start_date: startDate,
        event_start_date: startDate,
        show_end_date: computedEnd,
        event_end_date: computedEnd,
        site_location_id: selectedSiteId ?? null,
        customer_pickup: customerPickup,
        client_id: selectedClient?.id ?? null,
      };
      const scheduleDefaults = computeWarehouseScheduleDefaults(startDate, computedEnd);
      if (scheduleDefaults) {
        Object.assign(eventPayload, scheduleDefaults);
      }
      const venueSelection = createVenueMode
        ? { venueId: null, createVenue: newVenue }
        : { venueId: selectedVenue?.id ?? null, createVenue: null };
      const priceCheckLines = (initialPriceCheckDraft?.lines ?? []).map((line) => ({
        itemId: line.itemId,
        productId: line.productId ?? null,
        description: line.itemName,
        qty: line.qty,
        chargeDays: line.chargeDays,
        unitPrice: line.unitPrice ?? 0,
        usageRoomDate: line.usageRoomDate || startDate || new Date().toISOString().slice(0, 10),
        itemCode: line.itemCode,
        productCode: line.productCode,
        note: line.note ?? null,
      }));
      return createEventBundle({
        event: eventPayload,
        venueSelection,
        rooms: rooms as RoomDraft[],
        eventLines: priceCheckLines,
      });
    },
  });

  async function finish() {
    if (submitInFlightRef.current) return;
    if (dateErrors.start || dateErrors.end) {
      setDatesAttempted(true);
      setStep(0);
      return;
    }
    submitInFlightRef.current = true;
    setSaving(true);
    setError(undefined);
    try {
      const { event, error: evErr } = await createEventMutation.mutateAsync();
      if (evErr) throw evErr;
      if (claimedCodeRef.current && claimedCodeRef.current.code === eventCode.trim()) {
        await consumeOrderCodeHoldLocal();
      }
      onCreated(event);
    } catch (e: any) {
      setError(e?.message || 'Failed to create event');
    }
    setSaving(false);
    submitInFlightRef.current = false;
  }

  const selectedSiteLabel = React.useMemo(() => {
    if (!selectedSiteId) return null;
    const match = siteOptions.find((opt) => opt.location_id === selectedSiteId);
    return match ? match.name : null;
  }, [siteOptions, selectedSiteId]);
  const selectedSiteSupportsCustomerPickup = React.useMemo(
    () => siteOptions.find(option => option.location_id === selectedSiteId)?.customer_pickup_enabled === true,
    [siteOptions, selectedSiteId],
  );

  // Show loading state while determining if prompt is needed
  if (siteOptionsQuery.error) {
    return <CreateRecordWizard title="Create Event" onClose={handleCancel}>
      <div className="alert alert-error" role="alert">Locations could not be loaded. Your draft is still here.</div>
      <button type="button" className="btn" onClick={() => { void siteOptionsQuery.refetch(); }}>Retry</button>
    </CreateRecordWizard>;
  }
  if (needsLocationPrompt === null || siteOptionsLoading) {
    return (
      <CreateRecordWizard title="Create Event" onClose={handleCancel} bodyClassName="wizard-body--loading">
        <p>Loading locations…</p>
      </CreateRecordWizard>
    );
  }

  // Show location selection prompt when "all locations" is selected
  if (needsLocationPrompt) {
    return (
      <CreateRecordWizard
        title="Create Event"
        onClose={handleCancel}
        footer={(
          <>
            <button type="button" className="btn-close" onClick={onCancel} aria-label="Cancel">&times;</button>
            <button className="btn btn-primary" disabled={!promptLocationId} onClick={handleConfirmLocation}>
              Continue →
            </button>
          </>
        )}
      >
        <div className="stack gap-md wizard-location-prompt">
          <div className="stack gap-xs">
            <h3 className="h4 m0">Please select a location</h3>
            <p className="text-sm fg-muted m0">
              You currently have "All locations" selected. Please choose a specific location to create this event from.
            </p>
          </div>
          <CreateWizardLocationSelect
            value={promptLocationId}
            onChange={setPromptLocationId}
            options={siteOptions.map(option => ({ id: option.location_id, label: option.name }))}
            label="Location"
            placeholder="Select a location…"
            required
          />
          {siteOptions.length === 0 ? (
            <p className="text-sm fg-warn">You don't have access to any locations. Please contact an administrator.</p>
          ) : null}
        </div>
      </CreateRecordWizard>
    );
  }

  return (
    <CreateRecordWizard
      title="Create Event"
      className="event-create-wizard"
      onClose={handleCancel}
      closeDisabled={saving}
      steps={steps}
      activeStep={step}
      progressLabel="Create event progress"
      footer={(
        <>
          <button type="button" className="btn btn-secondary event-create-cancel" onClick={handleCancel} disabled={saving}>Cancel</button>
          {step > 0 ? (
            <button className="btn btn-secondary" disabled={saving} onClick={() => setStep(current => current - 1)}>
              Back
            </button>
          ) : null}
          {step < steps.length - 1 ? (
            <button className="btn btn-primary" disabled={!canNext() || saving} onClick={nextStep}>
              Next
            </button>
          ) : null}
          {step === steps.length - 1 ? (
            <button className="btn btn-primary" disabled={saving} onClick={finish}>
              {saving ? 'Creating…' : 'Create event'}
            </button>
          ) : null}
        </>
      )}
    >
        {step === 0 && (
          <div className="event-create-sections">
          <section className="event-create-section" aria-labelledby="event-create-details">
            <h3 id="event-create-details" ref={stepHeadingRef} tabIndex={-1}>Event details</h3>
          <CreateWizardBasics className="event-create-basics">
            <label>
              Event Name *
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Annual conference" />
            </label>
            <CreateWizardClientPicker
              selectedClient={selectedClient}
              onChange={setSelectedClient}
              helperText="Optional. Add a client now or later."
            />
            <label>
              R2 Order Number *
              {isEditingCode ? (
                <div className="stack gap-xxs">
                  <input
                    value={codeInput}
                    onChange={(e) => {
                      setCodeInput(e.target.value);
                      setEventCodeError(null);
                    }}
                    placeholder="Enter order number"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    autoFocus
                  />
                  {eventCodeError && <span className="text-xxs fg-danger">{eventCodeError}</span>}
                  <div className="flex gap-xs">
                    <button
                      type="button"
                      className="btn-save"
                      disabled={codeSaving}
                      aria-label="Save"
                      onClick={async () => {
                        const next = (codeInput || '').trim();
                        if (!next) {
                          setEventCodeError('Required');
                          return;
                        }
                        if (next === (eventCode || '')) {
                          setIsEditingCode(false);
                          setEventCodeError(null);
                          return;
                        }
                        setCodeSaving(true);
                        try {
                          const conflict = await checkEventCodeExists(next, null);
                          if (conflict) {
                            const desc = conflict.name ? String(conflict.name) : 'another event';
                            setEventCodeError(`R2 Order Number already exists with ${desc}`);
                            return;
                          }
                          const held = claimedCodeRef.current;
                          if (held && held.code === eventCode && held.locationId === (selectedSiteId ?? '')) {
                            await releaseOrderCodeHold(held);
                          }
                          setEventCode(next);
                          setIsEditingCode(false);
                          setEventCodeError(null);
                          pushToast({ type: 'success', message: 'Event code saved' });
                        } catch (caught) {
                          setEventCodeError(caught instanceof Error ? caught.message : 'Unable to check that order number. Try again.');
                        } finally {
                          setCodeSaving(false);
                        }
                      }}
                    >
                      <SaveIcon />
                    </button>
                    <button
                      type="button"
                      className="btn xs subtle"
                      onClick={() => {
                        setIsEditingCode(false);
                        setEventCodeError(null);
                      }}
                      disabled={codeSaving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="stack gap-xxs">
                  <div className="flex gap-xs">
                    <input
                      value={eventCode}
                      readOnly
                      aria-readonly="true"
                      placeholder={eventCodeLoading ? 'Generating…' : selectedSiteId ? '' : 'Order numbers require a site'}
                      title={eventCode ? `Order numbers follow ${selectedSiteLabel || 'the selected site'} sequencing` : undefined}
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        background: 'var(--surface-soft)',
                        color: eventCode ? 'inherit' : 'var(--text-soft)',
                        cursor: 'not-allowed',
                      }}
                    />
                    <button
                      type="button"
                      className="btn xs"
                      onClick={() => {
                        setCodeInput(eventCode || '');
                        setIsEditingCode(true);
                        setEventCodeError(null);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                  {eventCodeLoading && (
                    <span className="text-xxs fg-muted">Generating the next number for {selectedSiteLabel || 'the selected site'}…</span>
                  )}
                  {eventCodeError && <span className="text-xxs fg-danger">{eventCodeError}</span>}
                  {!eventCodeLoading && !eventCodeError && eventCode && (
                    <span className="text-xxs fg-muted">Generated for the selected site. Edit if needed.</span>
                  )}
                </div>
              )}
            </label>
            <CreateWizardLocationSelect
              value={selectedSiteId}
              onChange={handleSelectSite}
              options={siteOptions.map(option => ({ id: option.location_id, label: option.name }))}
              required
              disabled={siteOptionsLoading}
            />
          </CreateWizardBasics>
          </section>
          <section className="event-create-section" aria-labelledby="event-create-dates">
            <h3 id="event-create-dates">Event dates</h3>
            <p className="event-create-help" id="event-create-dates-help">Both dates are required. Use the same date for a one-day event.</p>
            <CreateWizardBasics className="event-create-dates">
              <div className="event-create-date-field">
                <label htmlFor="event-create-start">Event start date *</label>
                <input id="event-create-start" ref={startInputRef} type="date" required value={eventStart} aria-invalid={datesAttempted && !!dateErrors.start} aria-describedby={datesAttempted && dateErrors.start ? 'event-create-start-error' : 'event-create-dates-help'} onChange={(e) => setEventStart(e.target.value)} />
                {datesAttempted && dateErrors.start ? <span className="event-create-field-error" id="event-create-start-error">{dateErrors.start}</span> : null}
              </div>
              <div className="event-create-date-field">
                <label htmlFor="event-create-end">Event end date *</label>
                <input id="event-create-end" ref={endInputRef} type="date" required value={eventEnd} min={eventStart || undefined} aria-invalid={datesAttempted && !!dateErrors.end} aria-describedby={datesAttempted && dateErrors.end ? 'event-create-end-error' : 'event-create-dates-help'} onChange={(e) => setEventEnd(e.target.value)} />
                {datesAttempted && dateErrors.end ? <span className="event-create-field-error" id="event-create-end-error">{dateErrors.end}</span> : null}
              </div>
            </CreateWizardBasics>
          </section>
          <section className="event-create-section">
            <fieldset className="wizard-fulfillment-method">
              <legend>Fulfillment method</legend>
              <label className={!customerPickup ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="fulfillment-method"
                  checked={!customerPickup}
                  onChange={() => setCustomerPickup(false)}
                />
                <span><strong>ITA Delivery / On-site Service</strong><small>Our team handles delivery and on-site service.</small></span>
              </label>
              <label className={customerPickup ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="fulfillment-method"
                  checked={customerPickup}
                  disabled={!selectedSiteSupportsCustomerPickup}
                  onChange={() => setCustomerPickup(true)}
                />
                <span><strong>Customer Pickup</strong><small>The customer collects and returns equipment at the warehouse.</small></span>
              </label>
              {!selectedSiteSupportsCustomerPickup ? (
                <p className="text-xxs fg-muted m0">Customer Pickup is not enabled for this warehouse.</p>
              ) : null}
            </fieldset>
          </section>
          </div>
        )}

        {step === 1 && (
          <div className="venue-step">
            <div className="event-create-step-intro">
              <h3 ref={stepHeadingRef} tabIndex={-1}>Event venue</h3>
              <p className="event-create-help">Choose where this event will take place.</p>
            </div>
            {customerPickup ? (
              <div className="alert alert-info">
                <strong>Venue is optional for Customer Pickup.</strong> Add one if it helps describe where the equipment will be used, or continue without one.
              </div>
            ) : null}
            {!createVenueMode && (
              <div className="venue-select-grid">
                <div className="venue-search">
                  <div className="venue-search-bar">
                    <div className="event-create-search-input">
                    <input
                      ref={venueSearchRef}
                      aria-label="Search venue library"
                      placeholder="Search venue library..."
                      value={venueSearch}
                      onChange={(e) => setVenueSearch(e.target.value)}
                      onCompositionStart={() => setVenueSearchComposing(true)}
                      onCompositionEnd={() => setVenueSearchComposing(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                          e.preventDefault();
                          setVenueQuery(venueSearch.trim());
                        }
                      }}
                    />
                    {venueSearch ? <button type="button" className="event-create-search-clear" aria-label="Clear venue search" onClick={() => { setVenueSearch(''); setVenueQuery(''); venueSearchRef.current?.focus(); }}>×</button> : null}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setCreateVenueMode(true);
                        venueChosenRef.current = true;
                        setSelectedVenue(null);
                      }}
                    >
                      New Venue
                    </button>
                  </div>
                  <ul className="venue-results" aria-label="Venue results" aria-busy={venueResultsQuery.isFetching}>
                    {venueResultsQuery.isFetching ? <li className="empty" role="status">Searching venues…</li> : null}
                    {venueResultsQuery.error ? <li className="empty" role="alert">Venues could not be loaded. <button type="button" className="btn xs" onClick={() => { void venueResultsQuery.refetch(); }}>Retry</button></li> : null}
                    {venueResults.map((v) => {
                      const isSelected = selectedVenue?.id === v.id;
                      return (
                        <li key={v.id} className={isSelected ? 'selected' : ''}>
                          <button type="button" className="venue-card" aria-pressed={isSelected} onClick={() => { venueChosenRef.current = true; setSelectedVenue(v); }}>
                            <div className="venue-card-head">
                              <div className="venue-card-title">{v.venue_name}</div>
                              <span className="venue-card-status">{isSelected ? 'Selected' : 'Select'}</span>
                            </div>
                            {(v.city || v.state) && <div className="venue-card-meta">{v.city}{v.city && v.state ? ', ' : ''}{v.state}</div>}
                            {v.is_outdoor && (
                              <div className="venue-card-tags">
                                <span className="venue-card-tag">Outdoor</span>
                              </div>
                            )}
                          </button>
                        </li>
                      );
                    })}
                    {!venueResultsQuery.isFetching && !venueResultsQuery.error && !venueResults.length && <li className="empty">No venues match your search.</li>}
                  </ul>
                </div>
                <div className="selected-venue-panel">
                  <div className="selected-venue-header">
                    <h4>Selected venue</h4>
                    {selectedVenue && (
                      <button type="button" className="btn btn-secondary" onClick={() => { venueChosenRef.current = true; setSelectedVenue(null); }}>
                        Clear
                      </button>
                    )}
                  </div>
                  {selectedVenue ? (
                    <div className="selected-venue-body">
                      <div className="selected-venue-name">{selectedVenue.venue_name}</div>
                      {(selectedVenue.city || selectedVenue.state) && (
                        <div className="selected-venue-meta">
                          {selectedVenue.city}
                          {selectedVenue.city && selectedVenue.state ? ', ' : ''}
                          {selectedVenue.state}
                        </div>
                      )}
                      {selectedVenue.is_outdoor && (
                        <div className="selected-venue-tags">
                          <span>Outdoor venue</span>
                        </div>
                      )}
                      <p className="selected-venue-tip">This venue will be attached to your event. Choose its rooms in the next step.</p>
                    </div>
                  ) : (
                    <div className="selected-venue-empty">
                      <p>Choose a venue from the library to attach it to this event. You can also create a new venue if it isn't in the list.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {createVenueMode && (
              <div className="create-venue-form">
                <div className="form-row-inline">
                  <label>
                    Name *
                    <input value={newVenue.venue_name} onChange={(e) => setNewVenue((v) => ({ ...v, venue_name: e.target.value }))} />
                  </label>
                  <label>
                    City
                    <input value={newVenue.city} onChange={(e) => setNewVenue((v) => ({ ...v, city: e.target.value }))} />
                  </label>
                  <label>
                    State
                    <input value={newVenue.state} onChange={(e) => setNewVenue((v) => ({ ...v, state: e.target.value }))} />
                  </label>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.8rem' }}>
                  <input
                    type="checkbox"
                    checked={newVenue.is_outdoor}
                    onChange={(e) => setNewVenue((v) => ({ ...v, is_outdoor: e.target.checked }))}
                  />{' '}
                  Outdoor Venue
                </label>
                <div className="hint">Save will happen when you finish the wizard.</div>
                <button type="button" className="btn btn-secondary" onClick={() => setCreateVenueMode(false)}>
                  {'\u2190'} Back to Library
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="rooms-step">
            <h3 ref={stepHeadingRef} tabIndex={-1}>Event rooms</h3>
            {roomTemplatesQuery.isFetching ? <p role="status">Loading venue rooms…</p> : null}
            {roomTemplatesQuery.error ? <div role="alert">Venue rooms could not be loaded. <button type="button" className="btn xs" onClick={() => { void roomTemplatesQuery.refetch(); }}>Retry</button></div> : null}
            {selectedVenue && roomTemplates.length > 0 && (
              <div className="room-templates">
                <h4 style={{ margin: '0 0 .5rem' }}>Template Rooms</h4>
                <div className="template-chips">
                  {roomTemplates.map((rt) => (
                    <button type="button" key={rt.id} className="chip" onClick={() => addTemplateRoom(rt)}>
                      {rt.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="add-room-inline">
              <input aria-label="Custom room name" placeholder="Add custom room name" value={roomName} onChange={(e) => setRoomName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); addCustomRoom(); } }} />
              <button type="button" className="btn btn-secondary" onClick={addCustomRoom}>
                Add
              </button>
            </div>
            <ul className="room-list">
              {rooms.map((r, i) => (
                <li key={i}>
                  <span>{r.name}</span>
                  <button type="button" onClick={() => removeRoom(i)} className="x" aria-label={`Remove ${r.name}`}>
                    ×
                  </button>
                </li>
              ))}
              {!rooms.length && <li className="empty">No rooms added yet.</li>}
            </ul>
            <div className="tip">You can always add or edit rooms later inside the event.</div>
          </div>
        )}

        {step === 3 && (
          <div className="review-step">
            <h3 ref={stepHeadingRef} tabIndex={-1}>Review event</h3>
            <div className="summary-grid">
              <div className="block">
                <strong>Name:</strong> {name}
              </div>
              <div className="block">
                <strong>R2 Order:</strong> {eventCode}
              </div>
              {eventStart && (
                <div className="block">
                  <strong>Event start date:</strong> {eventStart}
                </div>
              )}
              {eventEnd && (
                <div className="block">
                  <strong>Event end date:</strong> {eventEnd}
                </div>
              )}
              <div className="block">
                <strong>Site:</strong> {selectedSiteLabel}
              </div>
              <div className="block">
                <strong>Client:</strong> {selectedClient?.businessName || 'Not assigned'}
              </div>
              <div className="block">
                <strong>Venue:</strong> {createVenueMode ? newVenue.venue_name : selectedVenue?.venue_name || '-'}
              </div>
              <div className="block">
                <strong>Fulfillment:</strong> {customerPickup ? 'Customer Pickup' : 'ITA Delivery / On-site Service'}
              </div>
              <div className="block">
                <strong>Rooms:</strong> {rooms.length ? rooms.map((r) => r.name).join(', ') : 'None'}
              </div>
            </div>
            <div className="note">Create the event{selectedVenue || createVenueMode ? ', attach the venue' : ''} and add the selected rooms.</div>
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}
    </CreateRecordWizard>
  );
}
